import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { AiUsageRecordRow } from "../types/MeGenTypes";
import { RecordCostInfo } from "./aiModelCost";
import AiUsagePagination, { pageOf } from "./AiUsagePagination";
import { formatMetric, formatMoney, toLocalDateTime } from "./aiUsagePivot";
import { aggregateProjectUsage, NO_THREAD_PROJECT, ProjectCostSummary, ProjectUsageCost, UsageProjectIndex, UsageProjectProgress } from "./aiUsageProjects";

type Props = {
  records: AiUsageRecordRow[];
  index?: UsageProjectIndex;
  progress?: UsageProjectProgress;
  error?: "cancelled" | "inconsistent" | "request";
  threadTitles: Record<string, string>;
  userNameMap: Record<string, string>;
  costInfoOf: (row: AiUsageRecordRow) => RecordCostInfo;
  onRetry: () => void;
  onRecords: (title: string, records: AiUsageRecordRow[]) => void;
};

const cell: React.CSSProperties = { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", fontSize: "0.8rem", textAlign: "left" };
const numberCell: React.CSSProperties = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

const ProjectAmount: React.FC<{ summary: ProjectCostSummary }> = ({ summary }) => {
  const intl = useIntl();
  const unknown = summary.missingPriceCalls + summary.unknownQuantityCalls > 0;
  return <>
    <Typography component="span" sx={{ fontSize: "0.85rem", fontWeight: 700 }}>{formatMoney(summary.cost)}</Typography>
    {unknown && <Typography component="div" variant="caption" color="warning.main">{intl.formatMessage({ id: "megen.cost.projectCalculated" })}</Typography>}
    {summary.missingPriceCalls > 0 && <Typography component="div" variant="caption" color="warning.main">{intl.formatMessage({ id: "megen.cost.projectMissingPrice" }, { count: summary.missingPriceCalls })}</Typography>}
    {summary.unknownQuantityCalls > 0 && <Typography component="div" variant="caption" color="warning.main">{intl.formatMessage({ id: "megen.cost.projectUnknownQuantity" }, { count: summary.unknownQuantityCalls })}</Typography>}
    {summary.estimatedCalls > 0 && <Typography component="div" variant="caption" color="text.secondary">{intl.formatMessage({ id: "megen.cost.projectEstimated" }, { count: summary.estimatedCalls })}</Typography>}
  </>;
};

const ProjectThreads: React.FC<{ project: ProjectUsageCost; threadTitles: Props["threadTitles"]; onRecords: Props["onRecords"] }> = ({ project, threadTitles, onRecords }) => {
  const intl = useIntl();
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [project]);
  const nameOf = (threadId: string | null) => threadId ? threadTitles[threadId] || threadId : intl.formatMessage({ id: "megen.cost.projectNoThread" });
  return <Box role="region" aria-label={intl.formatMessage({ id: "megen.cost.projectThreads" })} sx={{ p: 1, backgroundColor: "#f8fafc" }}>
    <AiUsagePagination total={project.threads.length} page={page} onChange={setPage} />
    <Box sx={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }} aria-label={intl.formatMessage({ id: "megen.cost.projectThreads" })}>
        <thead><tr>
          <th style={cell}>{intl.formatMessage({ id: "megen.cost.projectThread" })}</th>
          <th style={numberCell}>{intl.formatMessage({ id: "megen.cost.projectCalls" })}</th>
          <th style={numberCell}>{intl.formatMessage({ id: "megen.cost.projectErrors" })}</th>
          <th style={numberCell}>{intl.formatMessage({ id: "megen.cost.projectCost" })}</th>
          <th style={cell}>{intl.formatMessage({ id: "megen.cost.projectLastUsed" })}</th>
        </tr></thead>
        <tbody>{pageOf(project.threads, page).map((thread) => <tr key={thread.key}>
          <td style={cell}><Button size="small" onClick={() => onRecords(nameOf(thread.threadId), thread.records)}>{nameOf(thread.threadId)}</Button>
            {thread.threadId && threadTitles[thread.threadId] && <Typography variant="caption" display="block">{thread.threadId}</Typography>}
          </td>
          <td style={numberCell}>{formatMetric(thread.calls)}</td>
          <td style={numberCell}>{formatMetric(thread.errors)}</td>
          <td style={numberCell}><ProjectAmount summary={thread} /></td>
          <td style={cell}>{toLocalDateTime(thread.lastUsed)}</td>
        </tr>)}</tbody>
      </table>
    </Box>
  </Box>;
};

const CardAiUsageProjects: React.FC<Props> = ({ records, index, progress, error, threadTitles, userNameMap, costInfoOf, onRetry, onRecords }) => {
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const projects = useMemo(() => aggregateProjectUsage(records, index, costInfoOf), [records, index, costInfoOf]);
  const nameOf = (project: ProjectUsageCost) => project.projectId !== null ? `${project.name || intl.formatMessage({ id: "megen.cost.projectName" })} (#${project.projectId})` : intl.formatMessage({ id: project.key === NO_THREAD_PROJECT ? "megen.cost.projectNoThread" : "megen.cost.projectUnmatched" });
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return projects;
    const matches = (text: string | null | undefined) => !!text?.toLowerCase().includes(query);
    return projects.filter((project) => {
      const bucketName = project.projectId !== null ? "" : intl.formatMessage({ id: project.key === NO_THREAD_PROJECT ? "megen.cost.projectNoThread" : "megen.cost.projectUnmatched" });
      return matches(project.name) || matches(project.projectId?.toString()) || matches(bucketName) ||
        project.threads.some((thread) => matches(thread.threadId) || matches(thread.threadId ? threadTitles[thread.threadId] : "")) ||
        project.records.some((row) => matches(row.user_id) || matches(row.user_id ? userNameMap[row.user_id] : ""));
    });
  }, [projects, search, threadTitles, userNameMap, intl]);
  useEffect(() => { setPage(0); setExpanded({}); }, [records, index]);
  useEffect(() => { setPage(0); }, [search]);
  const coverage = useMemo(() => {
    let total = 0;
    let matched = 0;
    for (const row of records) {
      if (!row.thread_id) continue;
      total++;
      if (index && Object.prototype.hasOwnProperty.call(index.threadProjects, row.thread_id)) matched++;
    }
    return { total, matched, noThread: records.length - total };
  }, [records, index]);
  return <Box sx={{ backgroundColor: "white", borderRadius: "0.75rem", p: 2.5, mb: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{intl.formatMessage({ id: "megen.cost.projectTab" })}</Typography>
    <Typography variant="caption" display="block" sx={{ mb: 1 }}>{intl.formatMessage({ id: "megen.cost.projectScope" })}</Typography>
    <Box role="status" sx={{ mb: 1 }}>
      <Typography variant="body2">{intl.formatMessage({ id: "megen.cost.projectCoverage" }, coverage)}</Typography>
      {progress?.loading && <Typography variant="caption">{intl.formatMessage({ id: "megen.cost.projectLoading" }, { projects: progress.projects, completed: progress.completed, total: progress.total ?? intl.formatMessage({ id: "megen.cost.unknownTotal" }) })}</Typography>}
    </Box>
    <Typography variant="caption" display="block" sx={{ mb: 1 }}>{intl.formatMessage({ id: "megen.cost.projectPermissions" })}</Typography>
    {(error || !index || !!index.failedProjects || !!index.conflictedThreads) && !progress?.loading && <Box role="alert" sx={{ mb: 1 }}>
      <Typography variant="body2" color="warning.main">{intl.formatMessage({ id: "megen.cost.projectIncomplete" }, { failed: index?.failedProjects || 0, conflicts: index?.conflictedThreads || 0 })}</Typography>
      <Button size="small" onClick={onRetry}>{intl.formatMessage({ id: "megen.cost.projectRetry" })}</Button>
    </Box>}
    <TextField size="small" label={intl.formatMessage({ id: "megen.cost.projectSearch" })} value={search} onChange={(event) => setSearch(event.target.value)} fullWidth />
    <AiUsagePagination total={filtered.length} page={page} onChange={setPage} />
    <Box sx={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }} aria-label={intl.formatMessage({ id: "megen.cost.projectTable" })}>
        <thead><tr>
          <th style={cell}>{intl.formatMessage({ id: "megen.cost.projectName" })}</th>
          <th style={numberCell}>{intl.formatMessage({ id: "megen.cost.projectThreads" })}</th>
          <th style={numberCell}>{intl.formatMessage({ id: "megen.cost.projectCalls" })}</th>
          <th style={numberCell}>{intl.formatMessage({ id: "megen.cost.projectErrors" })}</th>
          <th style={numberCell}>{intl.formatMessage({ id: "megen.cost.projectCost" })}</th>
          <th style={cell}>{intl.formatMessage({ id: "megen.cost.projectLastUsed" })}</th>
          <th style={cell}>{intl.formatMessage({ id: "megen.cost.projectRecords" })}</th>
        </tr></thead>
        <tbody>{pageOf(filtered, page).map((project) => <React.Fragment key={project.key}>
          <tr>
            <td style={cell}><Button size="small" aria-expanded={!!expanded[project.key]} onClick={() => setExpanded((prev) => ({ ...prev, [project.key]: !prev[project.key] }))}>{nameOf(project)}</Button></td>
            <td style={numberCell}>{formatMetric(project.threads.filter((thread) => thread.threadId).length)}</td>
            <td style={numberCell}>{formatMetric(project.calls)}</td>
            <td style={numberCell}>{formatMetric(project.errors)}</td>
            <td style={numberCell}><ProjectAmount summary={project} /></td>
            <td style={cell}>{toLocalDateTime(project.lastUsed)}</td>
            <td style={cell}><Button size="small" onClick={() => onRecords(nameOf(project), project.records)}>{intl.formatMessage({ id: "megen.cost.projectRecords" })}</Button></td>
          </tr>
          {expanded[project.key] && <tr><td colSpan={7} style={{ padding: 0 }}><ProjectThreads project={project} threadTitles={threadTitles} onRecords={onRecords} /></td></tr>}
        </React.Fragment>)}</tbody>
      </table>
    </Box>
    {!filtered.length && !!index && !progress?.loading && !error && !index.failedProjects && !index.conflictedThreads && <Typography variant="body2">{intl.formatMessage({ id: "megen.cost.projectEmpty" })}</Typography>}
  </Box>;
};

export default CardAiUsageProjects;
